"use client";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseHashTabOptions<TabId extends string> {
  /** Tab to show when the URL carries no hash, or one we do not recognize. */
  defaultTab: TabId;
  /**
   * Hold off on applying the hash until the caller is ready. Only needed when
   * `defaultTab` depends on data that is still loading — otherwise leave it
   * alone so the hash is applied on mount, before the tabs first render.
   */
  enabled?: boolean;
}

/**
 * Keeps a tab selection in the URL hash, so `/companies/<id>#billing` opens on
 * Billing and a refresh stays there.
 *
 * The hash is applied in an effect rather than read during render: Next.js
 * never sends the hash to the server, so a render-phase read would desync the
 * server and client markup. Selecting a tab uses `history.replaceState`, which
 * keeps the back button pointed at the previous page instead of walking back
 * through the tabs.
 *
 * Selection is keyed by string id, not by position, so a caller may hide tabs
 * (permissions, screen size) without shifting everyone else's deep links.
 *
 * Framework-agnostic on purpose — it touches `window.location` / `window.history`
 * directly rather than `next/navigation`, so the root `@vellum/ui` barrel stays
 * importable from the Vite micro apps.
 */
function useHashTab<TabId extends string>(
  tabIds: readonly TabId[],
  { defaultTab, enabled = true }: UseHashTabOptions<TabId>,
): [TabId, (tabId: TabId) => void] {
  const [selectedTab, setSelectedTab] = useState<TabId>(defaultTab);
  const initialTabApplied = useRef(false);

  // Callers pass a fresh array literal most of the time; hold it in a ref so
  // the effects below key off `enabled` alone.
  const tabIdsRef = useRef(tabIds);
  tabIdsRef.current = tabIds;

  const readHashTab = useCallback((): TabId | null => {
    const hash = window.location.hash.slice(1);
    return (tabIdsRef.current as readonly string[]).includes(hash)
      ? (hash as TabId)
      : null;
  }, []);

  // Apply the deep link once. An absent or unrecognized hash falls back to the
  // default tab and leaves the URL untouched.
  useEffect(() => {
    if (!enabled || initialTabApplied.current) return;
    initialTabApplied.current = true;
    setSelectedTab(readHashTab() ?? defaultTab);
  }, [enabled, defaultTab, readHashTab]);

  // An in-page anchor or a pasted link on the same route changes the hash
  // without a navigation, so React never re-renders on its own.
  useEffect(() => {
    if (!enabled) return;
    const applyHash = () => setSelectedTab(readHashTab() ?? defaultTab);
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, [enabled, defaultTab, readHashTab]);

  const selectTab = useCallback((tabId: TabId) => {
    setSelectedTab(tabId);
    window.history.replaceState(null, "", `#${tabId}`);
  }, []);

  return [selectedTab, selectTab];
}

export { useHashTab };
export type { UseHashTabOptions };
